import React from "react";
import { useParams } from "react-router-dom";

import { ProgramType } from "shared/enums/ProgramType";
import { useRegDetail } from "shared/context/regDetail";
import { useParticipantDetails } from "apps/myExperience/context/participantDetails";

import {
  useCreateSelectionRequest,
  useDeleteSelectionRequest,
  useGetActivitiesForParticipant,
  useUpdateSelectionRequest
} from "./dataloader";
import { associatedProgramBehaviourFactory } from "apps/console/modules/program/workflow/associateProgram/profile/activity/details/components/associatedProgramBehaviourFactory";
import useLogAnalyticsScreenHook from "apps/myExperience/context/analyticsWrapper/hooks/useLogAnalyticsScreenHook";
import { AnalyticsScreenNameEnum } from "shared/enums/AnalyticsEnums";

export function useActivityListForParticipantsService() {
  const { id: registrationId } = useRegDetail();
  const { eventParticipantId = "" } = useParams();
  const { getProgramDetailsByEventParticipantIdAndProgramType, refreshIfActionPendingInProgram } =
    useParticipantDetails();

  const programId = React.useMemo(() => {
    return (
      (eventParticipantId &&
        getProgramDetailsByEventParticipantIdAndProgramType(eventParticipantId, ProgramType.ASSOCIATED_PROGRAM)?.id) ??
      ""
    );
  }, [eventParticipantId, getProgramDetailsByEventParticipantIdAndProgramType]);

  useLogAnalyticsScreenHook({
    properties: { screenType: AnalyticsScreenNameEnum.SELECTION_ACTIVITIES, programId, eventParticipantId }
  });

  const { mutateAsync: createSelectionRequest } = useCreateSelectionRequest({ programId, eventParticipantId });
  const { mutateAsync: updateSelectionRequest } = useUpdateSelectionRequest({ programId, eventParticipantId });
  const { mutateAsync: deleteSelectionRequest } = useDeleteSelectionRequest({ programId, eventParticipantId });

  const { data: participantActivityList = [] } = useGetActivitiesForParticipant({
    programId,
    eventParticipantId
  });

  const config = React.useMemo(() => {
    return associatedProgramBehaviourFactory();
  }, []);

  const createSelectionRequestHandler = React.useCallback(
    ({ timeSlots, activityId }) => createSelectionRequest({ timeSlots, activityId, eventParticipantId }),
    [eventParticipantId, createSelectionRequest]
  );

  const updateSelectionRequestHandler = React.useCallback(
    ({ timeSlots, selectionId, uiVersion }) =>
      updateSelectionRequest({ timeSlots, id: selectionId, uiVersion, eventParticipantId }),
    [eventParticipantId, updateSelectionRequest]
  );

  const deleteSelectionRequestHandler = React.useCallback(
    selectionId => deleteSelectionRequest({ pathParams: { id: selectionId } }),
    [deleteSelectionRequest]
  );

  const refreshMyActions = React.useCallback(
    () => refreshIfActionPendingInProgram(programId),
    [programId, refreshIfActionPendingInProgram]
  );

  return {
    config,
    programId,
    registrationId,
    refreshMyActions,
    createSelectionRequestHandler,
    updateSelectionRequestHandler,
    deleteSelectionRequestHandler,
    hasProgramWriteAuthority: false,
    associatedProgramActivities: config.trasformActivitiesForParticipantResponse(participantActivityList)
  };
}
